import React, { useCallback } from 'react';
import { DateTime } from 'luxon';
import { useDispatch } from 'react-redux';
import { updateDateForReadingPlan, useActiveInstantiatedPlans } from '@soulhx/client-store';
import { getIpDateRange } from '@soulhx/fs-common';
import { useTranslation } from 'react-i18next';
import { type CalendarCalloutItem } from '../common/calendar/helpers/dm.js';
import { CalendarWidget } from '../common/calendar/CalendarWidget.js';

const DayStatus = {
  completed: 'Completed',
  partiallyCompleted: 'Partially Completed',
  notCompleted: 'Not Completed',
} as const;

// prettier-ignore
type DayStatus = typeof DayStatus[keyof typeof DayStatus];

interface IPlanCalendarView {
  dateToShow: DateTime;
  clickHandler?: () => void;
}
export const PlanCalendarView = ({ dateToShow, clickHandler }: IPlanCalendarView) => {
  const { data } = useActiveInstantiatedPlans();
  const dispatch = useDispatch();
  const { t } = useTranslation(['plan']);

  const dayClickedInCalendar = useCallback(
    (value: Date) => {
      const dateToDispatch = DateTime.fromJSDate(value);
      dispatch(updateDateForReadingPlan(dateToDispatch.toISODate()!));
      if (clickHandler) clickHandler();
    },
    [dispatch, clickHandler]
  );

  const dayList = new Map<string, DayStatus>();

  for (const p of data) {
    for (const d of p.days) {
      const currentDate = d.scheduledDate;
      const currentDayCompleted = d.completed;

      if (!dayList.has(currentDate)) {
        dayList.set(currentDate, currentDayCompleted ? DayStatus.completed : DayStatus.notCompleted);
      } else {
        const stateInMap = dayList.get(currentDate);
        let newState: DayStatus;

        if (currentDayCompleted) {
          if (stateInMap === DayStatus.completed) {
            newState = DayStatus.completed;
          } else {
            newState = DayStatus.partiallyCompleted;
          }
        } else {
          if (stateInMap === DayStatus.completed || stateInMap === DayStatus.partiallyCompleted) {
            newState = DayStatus.partiallyCompleted;
          } else {
            newState = DayStatus.notCompleted;
          }
        }

        dayList.set(currentDate, newState);
      }
    }
  }

  const completedCallout: CalendarCalloutItem = {
    name: t('plan:calendar.completedCategory'),
    dates: new Set<string>(),
  };
  const partialsCallout: CalendarCalloutItem = {
    name: t('plan:calendar.partialCategory'),
    dates: new Set<string>(),
  };
  const incompleteCallout: CalendarCalloutItem = {
    name: t('plan:calendar.incompleteCategory'),
    dates: new Set<string>(),
  };

  dayList.forEach((value, key) => {
    if (value === DayStatus.completed) {
      completedCallout.dates.add(key);
    }
    if (value === DayStatus.partiallyCompleted) {
      partialsCallout.dates.add(key);
    }
    if (value === DayStatus.notCompleted) {
      incompleteCallout.dates.add(key);
    }
  });

  const callouts: CalendarCalloutItem[] = [completedCallout, partialsCallout, incompleteCallout];

  const range = getIpDateRange(data.flatMap((p) => p.days));
  const earliestDate = range ? DateTime.fromISO(range.start) : DateTime.now();
  const latestDate = range ? DateTime.fromISO(range.end) : DateTime.now();

  return (
    <CalendarWidget
      dateToShow={dateToShow}
      selectNewDate={dayClickedInCalendar}
      startDateChange={() => {}}
      minDate={earliestDate}
      maxDate={latestDate}
      callouts={callouts}
      hardcodedSelectedValues={[
        t('plan:calendar.completedCategory'),
        t('plan:calendar.partialCategory'),
        t('plan:calendar.incompleteCategory'),
      ]}
    />
  );
};
